import { toNumber } from 'shared/lib/number/number';

import {
    type ReportWidgetItem,
    type ReportWidgetTooltipArgs,
    type ReportWidgetTooltipRowConfig,
    getChartItemId,
} from '../types';

interface TooltipResolvedPoint {
    item: ReportWidgetItem | null;
    name: string;
    color?: string;
}

/**
 * Resolve tooltip metadata from the raw Recharts payload
 * - best-effort `ReportWidgetItem` (may be null)
 * - display name/label
 * - series color
 */
export function getTooltipPointFromPayload(
    args: ReportWidgetTooltipArgs,
): TooltipResolvedPoint | null {
    const { payload, label, items } = args;

    if (!payload || payload.length === 0) {
        return null;
    }

    const entry = payload[0];
    const pointPayload = entry.payload as {
        id?: string;
        name?: string;
        color?: string;
    } | null;

    const name = String(pointPayload?.name ?? entry.name ?? label ?? '');
    const color = pointPayload?.color ?? entry.color ?? undefined;

    let item: ReportWidgetItem | null = null;

    const itemId = pointPayload?.id;
    if (itemId) {
        item =
            items.find((candidate) => getChartItemId(candidate) === itemId) ??
            null;
    }

    if (!item && pointPayload?.name) {
        item =
            items.find(
                (candidate) => candidate.label === String(pointPayload.name),
            ) ?? null;
    }

    // For aggregated "Other" slices we do not want to show tooltips at all.
    if (item?.meta?.isAggregatedOther) {
        return null;
    }

    return {
        item,
        name,
        color,
    };
}

/**
 * Convenience builder for a standard "Savings" row:
 * bold label, bold positive value, using the provided number formatter.
 */
export function buildSavingsRow(params: {
    id?: string;
    label: string;
    amount: number;
    total?: number;
    valueFormatter: (value: number) => string;
}): ReportWidgetTooltipRowConfig {
    const { id = 'savings', label, amount, total, valueFormatter } = params;

    const formatted = valueFormatter(amount);
    const percent =
        typeof total === 'number' && total > 0
            ? ((amount / total) * 100).toFixed(1)
            : undefined;
    const value = percent ? `${formatted} (${percent}%)` : formatted;

    return {
        id,
        variant: 'labelValue',
        label,
        boldLabel: true,
        value,
        boldValue: true,
        positiveValue: true,
    };
}

/**
 * Default tooltip row builder used when a widget does not provide a custom `tooltipRowsBuilder`.
 * Produces a simple header row (label) followed by a single value row that can optionally
 * include a percentage when a `total` is supplied.
 */
export function buildGenericTooltipRows(
    args: ReportWidgetTooltipArgs,
): ReportWidgetTooltipRowConfig[] {
    const { payload, valueFormatter, label, items, total } = args;

    const resolved = getTooltipPointFromPayload({
        payload,
        label,
        valueFormatter,
        items,
        total,
    });

    if (!payload || payload.length === 0 || !resolved) {
        return [];
    }

    const entry = payload[0];
    const value = toNumber(entry.value as number | string | null);

    const formattedValue = valueFormatter(value);
    const totalValue = typeof total === 'number' ? total : 0;
    const percent =
        totalValue > 0 ? ((value / totalValue) * 100).toFixed(1) : undefined;
    const valueLine = percent
        ? `${formattedValue} (${percent}%)`
        : formattedValue;

    return [
        {
            id: 'header',
            variant: 'header',
            label: resolved.name,
            boldLabel: true,
            mutedLabel: true,
            marginBottom: 4,
        },
        {
            id: 'primary',
            variant: 'labelValue',
            value: valueLine,
            valueColor: resolved.color ?? 'text.secondary',
        },
    ];
}
